import Dexie, { type EntityTable } from "dexie";
import { z } from "zod";
import type { BackupFile, CreativeProject } from "./domain";
import { emptyProjectDraft, seedProjects } from "./seed";

class AuthorStudioDatabase extends Dexie {
  projects!: EntityTable<CreativeProject, "id">;

  constructor() {
    super("author-studio");
    this.version(1).stores({ projects: "id, status, updatedAt" });
  }
}

const db = new AuthorStudioDatabase();
let projectSaveQueue = Promise.resolve();

const languageSchema = z.enum(["si-LK", "en-GB", "und"]);
const versionSchema = z.object({
  id: z.string().min(1),
  number: z.number().int().positive(),
  name: z.string(),
  text: z.string(),
  createdAt: z.string().datetime(),
  reason: z.enum(["initial", "saved", "restored", "continued"]),
  basedOnVersionId: z.string().optional(),
});
const editionSchema = z.object({
  id: z.string().min(1),
  language: languageSchema,
  label: z.string(),
  text: z.string(),
  sourceVersionId: z.string().min(1),
  approach: z.enum([
    "Empty edition",
    "Copied source",
    "Author-supplied translation",
    "Sample content",
    "Preserve meaning closely",
    "Natural translation",
  ]),
});
const panelSchema = z.object({
  id: z.string().min(1),
  visual: z.string(),
  narration: z.string(),
  speaker: z.string().optional(),
  dialogue: z.string().optional(),
});
const sceneSchema = z.object({
  id: z.string().min(1),
  title: z.string(),
  setting: z.string(),
  emotion: z.string(),
  panels: z.array(panelSchema),
});
const storyboardSchema = z.object({
  id: z.string().min(1),
  title: z.string(),
  sourceVersionId: z.string().min(1),
  scenes: z.array(sceneSchema),
});
const projectSchema = z.object({
  id: z.string().min(1),
  title: z.string().min(1),
  workType: z.enum(["Fiction", "Non-fiction", "Children's story", "Not sure yet"]),
  status: z.enum(["active", "archived"]),
  originalLanguage: languageSchema,
  creationMethod: z.enum(["write", "tell"]),
  audience: z.string().min(1),
  tone: z.string().min(1),
  tones: z.array(z.string()).optional(),
  feelings: z.array(z.string()),
  customTone: z.string().optional(),
  customFeeling: z.string().optional(),
  emotionalDirection: z.string().optional(),
  assistanceLevel: z.enum([
    "Keep my words",
    "Help me improve it",
    "Develop my ideas",
    "Create a first draft",
    "Create with me",
    "Build it with me",
  ]),
  preserveWording: z.boolean(),
  preserveCulture: z.boolean(),
  collaborationAnswers: z.object({
    centre: z.string(),
    change: z.string(),
    challenge: z.string(),
    ending: z.string(),
    preserveExactly: z.string(),
  }).optional(),
  originalInput: z.string(),
  workingText: z.string(),
  versions: z.array(versionSchema).min(1),
  editions: z.array(editionSchema),
  storyboards: z.array(storyboardSchema),
  updatedAt: z.string().datetime(),
});

const draftSchema = z.object({
  workType: z.enum(["Fiction", "Non-fiction", "Children's story", "Not sure yet"]),
  audience: z.string(),
  tones: z.array(z.string()),
  feelings: z.array(z.string()),
  customTone: z.string(),
  customFeeling: z.string(),
  emotionalDirection: z.string(),
  assistanceLevel: z.enum(["Keep my words", "Help me improve it", "Develop my ideas", "Create a first draft", "Build it with me"]),
  originalLanguage: languageSchema,
  languageManuallySelected: z.boolean(),
  preserveWording: z.boolean(),
  preserveCulture: z.boolean(),
  idea: z.string(),
  stage: z.enum(["capture", "assistance", "direction", "review"]),
  savedAt: z.string(),
  suggestedFields: z.array(z.string()),
  collaborationAnswers: z.object({
    centre: z.string(),
    change: z.string(),
    challenge: z.string(),
    ending: z.string(),
    preserveExactly: z.string(),
  }).optional(),
});

const backupSchema = z.object({
  format: z.literal("author-studio-backup"),
  version: z.union([z.literal(1), z.literal(2)]),
  exportedAt: z.string().datetime(),
  projects: z.array(projectSchema),
  unfinishedWork: draftSchema.optional(),
});

export function normalizeProject(value: z.infer<typeof projectSchema>): CreativeProject {
  const legacyPlaceholder = "මෙය ප්‍රධාන කෘතියට සම්බන්ධ කෙටි සිංහල ආදර්ශ පෙළකි. භාවිතයට පෙර සිංහල භාෂාව හොඳින් දන්නා අයෙකුගේ සමාලෝචනය අවශ්‍ය වේ.";
  const legacyEnglishPlaceholder = "This is a short demonstration English edition linked to the latest master version. A fluent speaker must review the meaning, cultural expressions, and wording before real use.";
  return {
    ...value,
    tones: value.tones ?? (value.tone ? [value.tone] : []),
    customTone: value.customTone ?? "",
    customFeeling: value.customFeeling ?? "",
    emotionalDirection: value.emotionalDirection ?? "",
    collaborationAnswers: {
      ...emptyProjectDraft().collaborationAnswers,
      ...value.collaborationAnswers,
    },
    assistanceLevel: value.assistanceLevel === "Create with me" ? "Build it with me" : value.assistanceLevel,
    editions: value.editions.map((edition) =>
      edition.text === legacyPlaceholder || edition.text === legacyEnglishPlaceholder
        ? { ...edition, approach: "Sample content" as const }
        : edition
    ),
  };
}

export interface ProjectRepository {
  list(): Promise<CreativeProject[]>;
  save(project: CreativeProject): Promise<void>;
  replaceAll(projects: CreativeProject[]): Promise<void>;
}

export const projectRepository: ProjectRepository = {
  async list() {
    const existing = await db.projects.toArray();
    if (existing.length) return existing.map((project) => normalizeProject(project));
    await db.projects.bulkPut(seedProjects);
    return seedProjects;
  },
  async save(project) {
    const write = projectSaveQueue.then(() => db.projects.put(project));
    projectSaveQueue = write.then(() => undefined, () => undefined);
    await write;
  },
  async replaceAll(projects) {
    await db.transaction("rw", db.projects, async () => {
      await db.projects.clear();
      await db.projects.bulkPut(projects);
    });
  },
};

export async function readBackupFile(file: File): Promise<BackupFile> {
  if (file.size > 10 * 1024 * 1024) {
    throw new Error("Backup file is too large");
  }
  const text = await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error ?? new Error("Could not read backup"));
    reader.onload = () => resolve(String(reader.result ?? ""));
    reader.readAsText(file);
  });
  const parsed: unknown = JSON.parse(text);
  const backup = backupSchema.parse(parsed);
  return {
    ...backup,
    projects: backup.projects.map(normalizeProject),
    unfinishedWork: backup.unfinishedWork
      ? {
          ...emptyProjectDraft(),
          ...backup.unfinishedWork,
          collaborationAnswers: {
            ...emptyProjectDraft().collaborationAnswers,
            ...backup.unfinishedWork.collaborationAnswers,
          },
        }
      : undefined,
  };
}
